// Registry of the Remix icons bundled with the site (see Icon.tsx).
// Kept in a plain module so helper functions can be imported without
// affecting React Fast Refresh of the Icon component.

import type { ComponentType } from "react";
import {
  RiAccountCircleLine,
  RiAddLine,
  RiAlertLine,
  RiArchiveLine,
  RiArrowDownLine,
  RiArrowDownSLine,
  RiArrowGoBackLine,
  RiArrowLeftLine,
  RiArrowLeftSLine,
  RiArrowRightLine,
  RiArrowRightSLine,
  RiArrowUpLine,
  RiArrowUpSLine,
  RiAwardLine,
  RiBarChart2Line,
  RiBookOpenLine,
  RiBuilding4Line,
  RiCalendarCheckLine,
  RiCalendarEventLine,
  RiCalendarLine,
  RiChat1Fill,
  RiCheckDoubleLine,
  RiCheckLine,
  RiCheckboxCircleLine,
  RiCloseCircleLine,
  RiCloseLine,
  RiCloudLine,
  RiCodeBoxLine,
  RiCpuLine,
  RiDashboard3Line,
  RiDatabase2Line,
  RiDeleteBinLine,
  RiDownload2Line,
  RiDownloadCloud2Line,
  RiDownloadLine,
  RiEditLine,
  RiErrorWarningLine,
  RiExchangeLine,
  RiExternalLinkLine,
  RiEyeLine,
  RiEyeOffLine,
  RiFacebookCircleFill,
  RiFacebookFill,
  RiFileCopyLine,
  RiFileList3Line,
  RiFileListLine,
  RiFilePdfLine,
  RiFilePptLine,
  RiFileSearchLine,
  RiFileTextLine,
  RiFilter3Line,
  RiFlagLine,
  RiFlaskLine,
  RiFocus3Line,
  RiGlobalLine,
  RiGovernmentLine,
  RiGraduationCapLine,
  RiHardDrive2Line,
  RiHashtag,
  RiHeart3Line,
  RiHistoryLine,
  RiImageLine,
  RiInboxLine,
  RiInformationLine,
  RiKey2Line,
  RiLightbulbFlashLine,
  RiLinkM,
  RiLinkedinBoxFill,
  RiLinkedinFill,
  RiLinksLine,
  RiListCheck,
  RiLoader4Line,
  RiLockLine,
  RiLockPasswordLine,
  RiLoginBoxLine,
  RiLogoutBoxRLine,
  RiMailLine,
  RiMailOpenLine,
  RiMailSendLine,
  RiMapPinLine,
  RiMegaphoneLine,
  RiMenuLine,
  RiMicLine,
  RiMore2Fill,
  RiNewspaperLine,
  RiPhoneLine,
  RiPlayCircleLine,
  RiPlugLine,
  RiPresentationLine,
  RiPriceTag3Line,
  RiQuestionAnswerLine,
  RiQuestionLine,
  RiRefreshLine,
  RiRestartLine,
  RiRoadMapLine,
  RiSave3Line,
  RiSchoolLine,
  RiSearchLine,
  RiSeedlingLine,
  RiSendPlaneLine,
  RiServerLine,
  RiServiceLine,
  RiSettings3Line,
  RiShareForwardLine,
  RiShieldCheckLine,
  RiShieldKeyholeLine,
  RiShieldUserLine,
  RiSparklingLine,
  RiSpamLine,
  RiStarFill,
  RiStarLine,
  RiSubtractLine,
  RiTeamLine,
  RiTerminalBoxLine,
  RiTimeLine,
  RiToolsLine,
  RiUserAddLine,
  RiUserLine,
  RiUserSettingsLine,
  RiUserStarLine,
  RiVideoOnLine,
  RiVipCrownLine,
  RiYoutubeFill,
  RiUpload2Line,
  RiFolderOpenLine,
  RiFile3Line,
} from "@remixicon/react";

export type IconComponent = ComponentType<{ size?: number | string; className?: string }>;

/**
 * Only icons actually used by the site are imported by name above (rather
 * than the whole icon set) so bundlers can tree-shake the rest. When a new
 * `ri-...` class is introduced anywhere in src/, add its matching `Ri...`
 * import and map entry here. tests/unit/icons.test.ts fails when a token used
 * in src/ is missing.
 */
export const iconMap: Record<string, IconComponent> = {
  RiAccountCircleLine,
  RiAddLine,
  RiAlertLine,
  RiArchiveLine,
  RiArrowDownLine,
  RiArrowDownSLine,
  RiArrowGoBackLine,
  RiArrowLeftLine,
  RiArrowLeftSLine,
  RiArrowRightLine,
  RiArrowRightSLine,
  RiArrowUpLine,
  RiArrowUpSLine,
  RiAwardLine,
  RiBarChart2Line,
  RiBookOpenLine,
  RiBuilding4Line,
  RiCalendarCheckLine,
  RiCalendarEventLine,
  RiCalendarLine,
  RiChat1Fill,
  RiCheckDoubleLine,
  RiCheckLine,
  RiCheckboxCircleLine,
  RiCloseCircleLine,
  RiCloseLine,
  RiCloudLine,
  RiCodeBoxLine,
  RiCpuLine,
  RiDashboard3Line,
  RiDatabase2Line,
  RiDeleteBinLine,
  RiDownload2Line,
  RiDownloadCloud2Line,
  RiDownloadLine,
  RiEditLine,
  RiErrorWarningLine,
  RiExchangeLine,
  RiExternalLinkLine,
  RiEyeLine,
  RiEyeOffLine,
  RiFacebookCircleFill,
  RiFacebookFill,
  RiFileCopyLine,
  RiFileList3Line,
  RiFileListLine,
  RiFilePdfLine,
  RiFilePptLine,
  RiFileSearchLine,
  RiFileTextLine,
  RiFilter3Line,
  RiFlagLine,
  RiFlaskLine,
  RiFocus3Line,
  RiGlobalLine,
  RiGovernmentLine,
  RiGraduationCapLine,
  RiHardDrive2Line,
  RiHashtag,
  RiHeart3Line,
  RiHistoryLine,
  RiImageLine,
  RiInboxLine,
  RiInformationLine,
  RiKey2Line,
  RiLightbulbFlashLine,
  RiLinkM,
  RiLinkedinBoxFill,
  RiLinkedinFill,
  RiLinksLine,
  RiListCheck,
  RiLoader4Line,
  RiLockLine,
  RiLockPasswordLine,
  RiLoginBoxLine,
  RiLogoutBoxRLine,
  RiMailLine,
  RiMailOpenLine,
  RiMailSendLine,
  RiMapPinLine,
  RiMegaphoneLine,
  RiMenuLine,
  RiMicLine,
  RiMore2Fill,
  RiNewspaperLine,
  RiPhoneLine,
  RiPlayCircleLine,
  RiPlugLine,
  RiPresentationLine,
  RiPriceTag3Line,
  RiQuestionAnswerLine,
  RiQuestionLine,
  RiRefreshLine,
  RiRestartLine,
  RiRoadMapLine,
  RiSave3Line,
  RiSchoolLine,
  RiSearchLine,
  RiSeedlingLine,
  RiSendPlaneLine,
  RiServerLine,
  RiServiceLine,
  RiSettings3Line,
  RiShareForwardLine,
  RiShieldCheckLine,
  RiShieldKeyholeLine,
  RiShieldUserLine,
  RiSparklingLine,
  RiSpamLine,
  RiStarFill,
  RiStarLine,
  RiSubtractLine,
  RiTeamLine,
  RiTerminalBoxLine,
  RiTimeLine,
  RiToolsLine,
  RiUserAddLine,
  RiUserLine,
  RiUserSettingsLine,
  RiUserStarLine,
  RiVideoOnLine,
  RiVipCrownLine,
  RiYoutubeFill,
  RiUpload2Line,
  RiFolderOpenLine,
  RiFile3Line,
};

/** "ri-file-3-line" → "RiFile3Line" */
export function tokenToPascal(token: string): string {
  return (
    "Ri" +
    token
      .slice(3)
      .split("-")
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join("")
  );
}

/** "RiFile3Line" → "ri-file-3-line" */
export function pascalToToken(name: string): string {
  return (
    "ri" +
    name
      .slice(2)
      .replace(/([A-Z])/g, "-$1")
      .replace(/(\d+)/g, "-$1")
      .toLowerCase()
  );
}

export function resolveIcon(token?: string): IconComponent | null {
  if (!token || !token.startsWith("ri-")) return null;
  return iconMap[tokenToPascal(token)] ?? null;
}

/** True when the given `ri-...` token is registered. */
export function hasIcon(token: string): boolean {
  return resolveIcon(token) !== null;
}

/** Every registered token, e.g. for an icon picker. */
export function iconTokens(): string[] {
  return Object.keys(iconMap).map(pascalToToken).sort();
}
